// Handles the redirect back from Emergent Google Auth.
// Emergent redirects to `{origin}/dashboard#session_id=xxx` after successful login.
// We exchange that session_id for a KELTA JWT via /api/auth/google/session, then land on /dashboard.
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState(null);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    // Extract session_id from URL fragment: `#session_id=abc123`
    const hash = location.hash || window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");

    if (!sessionId) {
      setError("Missing session_id in redirect URL");
      return;
    }

    (async () => {
      try {
        await api.post("/auth/google/session", null, {
          headers: { "X-Session-ID": sessionId },
        });
        // Clear the hash so a browser refresh doesn't reprocess the session_id
        window.history.replaceState(null, "", window.location.pathname);
        await refresh();
        toast.success("Signed in with Google");
        navigate("/dashboard", { replace: true });
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || "Google sign-in failed");
      }
    })();
  }, [location.hash, navigate, refresh]);

  return (
    <div className="min-h-screen grid place-items-center bg-background">
      <div className="text-center space-y-3">
        {!error ? (
          <>
            <div className="w-10 h-10 border-4 border-brand-blue border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm text-muted-foreground">Signing you in…</p>
          </>
        ) : (
          <>
            <p className="text-destructive font-semibold">{error}</p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="text-sm text-brand-blue hover:underline"
              data-testid="google-auth-back-to-login"
            >
              Back to login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
